/**
 * (owner, Sep 11: "boats and in fact all vehicles should" have the depth the buildings have) A vehicle's height along its
 * length, as content: `height: [[start, end, metres], …]` — fractions of the length from the front, a plateau inside
 * each segment and a straight ramp from one segment's end to the next one's start; before the first and after the
 * last, that segment's height. The renderer cuts the body into strips along the length (one per plateau, a few per
 * ramp), lifts each by its height the way a roof is lifted, and stamps the body's own silhouette darkened underneath
 * for the sides. Pure functions here, so the profile has a test and the renderer stays a renderer.
 *
 * (owner, Oct 1: "taking into account the height & height offset parameters"; fix 2 of the car redesign) A segment may
 * carry two more numbers, `[start, end, metres, inset, below]`: the part above `below` metres stands `inset` metres in
 * from each long side — a car's glasshouse narrower than its doors (the tumblehome), a harvester's cab and crane on a
 * wider frame. The full width goes up to `below` (the belt line); the renderer draws the side elevation's lower part on
 * the outer wall and its upper part on the inset one, and the narrower top over it. Absent, the inset is 0 and `below`
 * is the height (one full-width box, as before). Along a ramp all three run straight from one segment to the next.
 */
export type HeightProfile = ([number, number, number] | [number, number, number, number] | [number, number, number, number, number])[];

/** a segment's inset (m from each long side) and the full-width height under it */
export const insetOf = (s: HeightProfile[number]): number => Math.max(0, s[3] ?? 0);
export const belowOf = (s: HeightProfile[number]): number => (insetOf(s) > 0 ? Math.min(s[2], s[4] ?? s[2] * 0.62) : s[2]);

/** what the profile says at fraction `t` (0 = front, 1 = back) of the length: the height, the inset, the full-width height */
export function layersAlong(prof: HeightProfile, t: number): { h: number; i: number; b: number } {
  const at = (s: HeightProfile[number]) => ({ h: s[2], i: insetOf(s), b: belowOf(s) });
  if (!prof.length) return { h: 0, i: 0, b: 0 };
  if (t <= prof[0][0]) return at(prof[0]);
  for (let k = 0; k < prof.length; k++) {
    const s = prof[k], [a, b] = s;
    if (t >= a && t <= b) return at(s);
    const n = prof[k + 1];
    if (n && t > b && t < n[0]) { const f = (t - b) / (n[0] - b), x = at(s), y = at(n); return { h: x.h + (y.h - x.h) * f, i: x.i + (y.i - x.i) * f, b: x.b + (y.b - x.b) * f }; }
  }
  return at(prof[prof.length - 1]);
}

/** the height at fraction `t` (0 = front, 1 = back) of the length */
export function heightAlong(prof: HeightProfile, t: number): number { return layersAlong(prof, t).h; }

/** the tallest point of the profile */
export function heightMax(prof: HeightProfile): number { let m = 0; for (const s of prof) if (s[2] > m) m = s[2]; return m; }
/** any segment with an inset (the renderer's two-layer path) */
export const hasInset = (prof: HeightProfile): boolean => prof.some((s) => insetOf(s) > 0);

/** a strip: its ends along the length, the height at each end — and (Oct 1) the inset and the full-width height at each end */
export interface Strip { t0: number; t1: number; h0: number; h1: number; i0: number; i1: number; b0: number; b1: number }

/**
 * The strips the body is drawn in: one per plateau (its height at both ends), a few per ramp between plateaus (each a
 * short slope), and the ends before the first and after the last segment as plateaus. Covers [0, 1] exactly, in order.
 * `ramp` is the fewest strips a ramp gets (the renderer's detail by size on screen: 1 when the car is small); (Oct 1, fix 3)
 * a steep ramp gets more — one per 12 cm it climbs, up to 12 — or a windscreen or a crane crossing it stair-steps.
 */
export function stripsOf(prof: HeightProfile, ramp = 3, fine = true): Strip[] {
  const out: Strip[] = [];
  const flat = (t0: number, t1: number, s: HeightProfile[number]): Strip => ({ t0, t1, h0: s[2], h1: s[2], i0: insetOf(s), i1: insetOf(s), b0: belowOf(s), b1: belowOf(s) });
  if (!prof.length) return [{ t0: 0, t1: 1, h0: 0, h1: 0, i0: 0, i1: 0, b0: 0, b1: 0 }];
  if (prof[0][0] > 0) out.push(flat(0, prof[0][0], prof[0]));
  for (let i = 0; i < prof.length; i++) {
    const s = prof[i], [a, b] = s;
    out.push(flat(a, b, s));
    const n = prof[i + 1];
    if (n && n[0] > b) {
      const climb = Math.max(Math.abs(n[2] - s[2]), Math.abs(belowOf(n) - belowOf(s)), Math.abs(insetOf(n) - insetOf(s)) * 2);
      // (perf, Oct 1) a ramp that does not climb is one strip; a steep one at most twice the detail the size on screen asks for
      const k = ramp <= 1 || climb < 0.01 ? 1 : fine ? Math.max(ramp, Math.min(ramp * 2, 12, Math.ceil(climb / 0.12))) : ramp; // (`fine` off: the graphics quality's simpler vehicles)
      for (let j = 0; j < k; j++) {
        const ta = b + ((n[0] - b) * j) / k, tb = b + ((n[0] - b) * (j + 1)) / k, la = layersAlong(prof, ta), lb = layersAlong(prof, tb);
        out.push({ t0: ta, t1: tb, h0: la.h, h1: lb.h, i0: la.i, i1: lb.i, b0: la.b, b1: lb.b });
      }
    }
  }
  const last = prof[prof.length - 1];
  if (last[1] < 1) out.push(flat(last[1], 1, last));
  return out;
}
